// 换配图对话框（任务 3.3）· 上传直传 Storage / 素材库双 tab
// 上传：api 申请签名 URL → supabase-js uploadToSignedUrl 直传 → 回传 path 引用；
// 预览与导出同步更新（导出时后端按 path 从 Storage 取图嵌入）。

import { useEffect, useRef, useState } from "react";
import { api, ApiError, type DeckImage } from "../../lib/api";
import { supabase } from "../../lib/supabase";

export interface PickedImage {
  assetId?: string;
  path: string;
  url: string;
}

interface ImageDialogProps {
  deckId: string;
  onClose: () => void;
  onPick: (img: PickedImage) => void;
}

export default function ImageDialog({ deckId, onClose, onPick }: ImageDialogProps) {
  const [tab, setTab] = useState<"upload" | "library">("upload");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [images, setImages] = useState<DeckImage[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tab === "library" && images === null) {
      api.listImages(deckId).then((r) => setImages(r.images)).catch((e) => setErr((e as Error).message));
    }
  }, [tab, deckId, images]);

  async function handleFile(f: File) {
    setBusy(true);
    setErr(null);
    try {
      // 1) 申请签名上传 URL + 资产台账
      const up = await api.createImageUpload(deckId, f.name);
      // 2) 直传 Storage
      const { error } = await supabase.storage.from("inputs").uploadToSignedUrl(up.path, up.token, f);
      if (error) throw new Error(error.message);
      // 3) 本地 objectURL 立即预览（持久预览 URL 由 GET deck 时服务端签发）
      onPick({ assetId: up.assetId, path: up.path, url: URL.createObjectURL(f) });
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-ink">替换配图</h3>
          <button className="text-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>

        <div className="mb-4 flex gap-2 border-b border-slate-200">
          {([["upload", "上传图片"], ["library", "素材库"]] as const).map(([k, label]) => (
            <button
              key={k}
              className={`px-3 py-1.5 text-sm font-medium ${tab === k ? "border-b-2 border-primary text-primary" : "text-muted"}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {err && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}

        {tab === "upload" && (
          <div className="text-center">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = "";
              }}
            />
            <button
              className="btn-primary w-full"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? "上传中…" : "选择本地图片（直传 Storage）"}
            </button>
            <p className="mt-2 text-xs text-muted">
              支持 png / jpg / webp；替换后在线预览立即更新，导出 pptx 时嵌入同一张图。
            </p>
          </div>
        )}

        {tab === "library" && (
          <div className="max-h-72 overflow-y-auto">
            {images === null && <p className="py-6 text-center text-sm text-muted">加载中…</p>}
            {images?.length === 0 && (
              <p className="py-6 text-center text-sm text-muted">素材库为空，先在「上传图片」里传一张。</p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {images?.map((img) => (
                <button
                  key={img.id}
                  className="group overflow-hidden rounded-lg ring-1 ring-slate-200 hover:ring-2 hover:ring-primary"
                  onClick={() => onPick({ assetId: img.id, path: img.path, url: img.url })}
                  title={img.filename}
                >
                  <img src={img.url} alt={img.filename} className="h-20 w-full object-cover" loading="lazy" />
                  <span className="block truncate px-1 py-0.5 text-[10px] text-muted">{img.filename}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
